"use client";

//region Imports
import React, {useEffect, useMemo, useRef, useState, useCallback} from "react";
import {useForm, useWatch} from "react-hook-form";
import _ from "lodash";

import {$core} from "@isudio/core";
import {$store} from "@isudio/globals";
import {pages, policies} from "@isudio/config";
import {useMergeRefs} from "@isudio/utils";
import {WorkflowActions} from "@isudio/data";
import type {IUserSignupResponse, IUserActivation, IActivatePayload, IOtpRequestPayload} from "@isudio/data";
import type {HeaderElement, FooterElement, BaseDialogProps} from "../";
import {LoadingSpinner, InputValidator, PasswordStrengthBar, ToolbarTop, ToolbarBottom, OtpInput, useDialogController, useTippyRegistry} from "../";
//endregion Imports

//region Types & Interfaces
interface ActivationProps extends BaseDialogProps {
  dialogOptions?: any;
  user: IUserSignupResponse;
  otp?: string;
  error?: any;
  onSuccess?: () => void;
  onClose?: () => void;
}
//endregion Types & Interfaces

export function Activation({
                             dialogOptions,
                             user,
                             otp,
                             error,
                             onSuccess,
                             onClose
                           }: ActivationProps) {
  //region Variables
  const [loading, setLoading] = useState(false);
  const [loadingLabel, setLoadingLabel] = useState<string | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const [footerHeight, setFooterHeight] = useState(0);
  const [isOverflow, setIsOverflow] = useState(false);
  const [isAtBottom, setIsAtBottom] = useState(false);
  const showFooter = !isOverflow || isAtBottom;
  const {name} = dialogOptions;
  const className = name.toLowerCase().replace(/ /g, "-");

  const photo: string = user?.photo?.thumbnail || _.get(pages.signup.mediaPicker, _.isDark() ? "defaultProfilePic" : "defaultProfilePicLight");
  const [showResend, setShowResend] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [isCounting, setIsCounting] = useState(false);
  const [resetKey, setResetKey] = useState(0);

  const userPolicy = _.get($store, `Data.userPolicy$.value.fields`, policies.userPolicy.fields);
  const defaults: IUserActivation = useMemo(() => ({
    email: user?.email || "",
    otp: otp || "",
    password: "",
    confirmPassword: ""
  }), [user?.email]);

  const {
    control,
    register,
    trigger,
    handleSubmit,
    setError,
    clearErrors,
    reset,
    getValues,
    setValue,
    formState: {
      isDirty,
      isValid,
      errors
    }
  } = useForm<IUserActivation>({
    criteriaMode: "all",
    defaultValues: defaults
  });

  const wOtp = useWatch({ control, name: "otp" });
  const wPassword = useWatch({ control, name: "password" });
  const wConfirmPassword = useWatch({ control, name: "confirmPassword" });
  //endregion Variables

  //region Form Validations
  const validations: any = {
    otp: {
      required: "OTP is required.",
      minLength: {
        value: userPolicy.otp.maxLength,
        message: `OTP should have at least ${userPolicy.otp.maxLength} characters.`,
      }
    },
    password: {
      required: "Password is required.",
      minLength: {
        value: userPolicy.password.minLength,
        message: `Password should have at least ${userPolicy.password.minLength} characters.`,
      }
    },
    confirmPassword: {
      required: "Password must be confirmed.",
      minLength: {
        value: userPolicy.password.minLength,
        message: `Password should have at least ${userPolicy.password.minLength} characters.`,
      },
      validate: (value: any) => {
        if (value !== wPassword) {
          return "Passwords do not match.";
        }
        return true;
      }
    }
  }
  //endregion Form Validations

  //region Form Registrations
  const otpRef = useRef<HTMLInputElement & {
    focusFirst?: () => void
  }>(null);
  const {
    ref: otpRegisterRef,
    onBlur: otpRegisterOnBlur,
    ...otpRegister
  } = register("otp", validations.otp);

  const passwordRef = useRef<HTMLInputElement | null>(null);
  const {
    ref: passwordRegisterRef,
    onBlur: passwordRegisterOnBlur,
    ...passwordRegister
  } = register("password", validations.password);

  const confirmPasswordRef = useRef<HTMLInputElement | null>(null);
  const {
    ref: confirmPasswordRegisterRef,
    onChange: confirmPasswordRegisterOnChange,
    onBlur: confirmPasswordRegisterOnBlur,
    ...confirmPasswordRegister
  } = register("confirmPassword", );
  //endregion Form Registrations

  //region Methods & Event Handlers
  const {
    closeDialog,
    setOnClose
  } = useDialogController();

  const {
    suppress,
    unsuppress
  } = useTippyRegistry();

  const checkPasswordMatch = () => {
    if ((wPassword.length < _.get(userPolicy, `password.minLength`, wPassword.length + 1)) ||
      (wConfirmPassword.length < _.get(userPolicy, `password.minLength`, wConfirmPassword.length + 1))) {
      clearErrors("confirmPassword");
      return;
    }

    if (wPassword !== wConfirmPassword) {
      setError("confirmPassword", {
        type: "validate",
        message: "Passwords do not match."
      });
    }
  };

  const startCountdown = () => {
    setSeconds(30);
    setIsCounting(true);
  };

  const onOtpRequest = async () => {
    if (isCounting) return;
    clearErrors("otp");
    suppress();
    startCountdown();

    setLoading(true);
    setLoadingLabel("Sending OTP");
    const payload: IOtpRequestPayload = _.omitBy({
      id: user.email,
      action: WorkflowActions.userActivation,
      device: {
        ...$store.Device.data || null,
        ...{deviceTime: new Date().toISOString()}
      }
    }, _.isNil) as IOtpRequestPayload;

    await $core.otpRequest(payload).then((data: IUserSignupResponse) => {
      $store.User.user$.next(data);
      setLoading(false);
      setLoadingLabel(null);
      void setValue("otp", "", {
        shouldValidate: false,
        shouldDirty: true
      });
      _.toastSuccess(`A new OTP has been sent to your registered email.\nPlease check and activate your account within ${userPolicy.otp.expiry} minutes.`);

    }).catch((e: any) => {
      setLoading(false);
      setLoadingLabel(null);
      _.toastError(_.get(e, 'message'));
    })

    unsuppress();
    const t = setTimeout(() => {
      otpRef.current?.focusFirst?.();
      clearTimeout(t);
    }, 10)
  };

  const onReset = () => {
    setResetKey((prev) => prev + 1);
    reset();
  };

  const _onKeyDown = async (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    e.stopPropagation();
    await _onSubmit();
  }

  const _onSubmit = async () => {
    if (!isValid) return;
    await handleSubmit(onSubmit)();
  }

  const onSubmit = async () => {
    setLoading(true);
    const payload: IActivatePayload = _.omitBy({
      uid: user.uid,
      email: user.email,
      otp: getValues("otp"),
      hash: _.sha512(getValues("password")),
      device: {
        ...$store.Device.data || null,
        ...{deviceTime: new Date().toISOString()}
      }
    }, _.isNil) as IActivatePayload;

    await $core.activate(payload).then(async (data: IUserSignupResponse) => {
      setLoading(false);
      $store.User.user$.next(data);
      await closeDialog(true);
      onSuccess?.();

    }).catch((e: any) => {
      setLoading(false);
      _.toastError(_.get(e, 'message'));
      if (['otpExpired', 'otpIncorrect'].indexOf(_.get(e, 'code')) > -1) {
        void setValue("otp", "", {
          shouldValidate: false,
          shouldDirty: true
        });
        setResetKey((prev) => prev + 1);
        setShowResend(true);
        const t = setTimeout(() => {
          otpRef.current?.focusFirst?.();
          clearTimeout(t);
        }, 10)
      }
    });
  };

  const onScroll = () => {
    const el = contentRef.current;
    if (!el) return;

    const distanceFromTop = el.scrollTop;

    const distanceFromBottom =
      el.scrollHeight - (el.scrollTop + el.clientHeight);

    const SHOW_THRESHOLD = 5;
    const HIDE_THRESHOLD = 25;
    const TOP_HIDE_THRESHOLD = 40;

    setIsAtBottom(prev => {
      // ✅ SHOW when near bottom
      if (!prev && distanceFromBottom <= SHOW_THRESHOLD) return true;

      // ✅ HIDE when near top (priority)
      if (distanceFromTop <= TOP_HIDE_THRESHOLD) return false;

      // ✅ HIDE when moving away from bottom
      if (prev && distanceFromBottom > HIDE_THRESHOLD) return false;

      return prev;
    });
  };

  const _onClose = useCallback(() => {
    onClose?.();
  }, [onClose]);
  //endregion Methods & Event Handlers

  //region Effects
  useEffect(() => {
    if (!footerRef.current) return;

    const el = footerRef.current;

    const updateHeight = () => {
      setFooterHeight(el.scrollHeight);
    };

    updateHeight();

    const observer = new ResizeObserver(() => updateHeight());
    observer.observe(el);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!contentRef.current) return;

    const el = contentRef.current;

    const checkOverflow = () => {
      requestAnimationFrame(() => {
        setIsOverflow(el.scrollHeight > el.clientHeight);
      });
    };

    checkOverflow();

    const observer = new ResizeObserver(() => checkOverflow());
    observer.observe(el);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    checkPasswordMatch();
  }, [wPassword]);

  useEffect(() => {
    checkPasswordMatch();
  }, [wConfirmPassword]);

  useEffect(() => {
    if (!isCounting) return;

    if (seconds === 0) {
      setIsCounting(false);
      return;
    }

    const timer = setInterval(() => {
      setSeconds((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [isCounting, seconds]);

  useEffect(() => {
    if (!_.isNothing(error)) {
      if (['otpExpired', 'otpIncorrect'].indexOf(_.get(error, 'code')) > -1) {
        setShowResend(true);
        setError("otp", {
          type: "validate",
          message: error?.message
        });
        otpRef.current?.focusFirst?.();
      }

    } else {
      if (!_.isNothing(otp)) {
        passwordRef.current?.focus();
      }
    }
  }, []);

  useEffect(() => {
    setOnClose(_onClose);
  }, [setOnClose, _onClose]);
  //endregion Effects

  //region Toolbar Elements
  const headerElements: HeaderElement[] = [
    {
      name: "title",
      type: "span",
      position: "left",
      label: pages.activation.title,
      style: "text-md md:text-lg text-white font-bold tracking-wider"
    },
    {
      name: "reset",
      type: "button",
      position: "right",
      icon: "fa-rotate",
      tooltip: "Reset",
      style: "text-white scale-90 transition duration-200 hover:text-generic-hover hover:scale-120",
      hidden: !isDirty,
      onClick: () => onReset()
    },
    {
      name: "close",
      type: "button",
      position: "right",
      icon: "fa-square-xmark",
      tooltip: "Close",
      style: "text-white scale-115 transition duration-200 hover:text-light-red hover:scale-125",
      onClick: () => closeDialog()
    }
  ];

  const footerElements: FooterElement[] = [
    {
      name: "activate",
      type: "gradientButton",
      action: "submit",
      label: "Activate",
      style: `btn-activate btn-${isValid ? 'blue transition duration-200 hover:text-generic-hover hover:scale-105' : 'gray'}`,
      disabled: !isValid,
      onClick: () => _onSubmit(),
      onEnter: () => _onSubmit()
    },
    {
      name: "cancel",
      type: "gradientButton",
      label: "Cancel",
      style: "btn-cancel btn-gray-reverse hover:text-light-red",
      onClick: () => closeDialog(),
    }
  ];
  //endregion Toolbar Elements

  //region HTML Elements
  const loadingSpinner = (
    <LoadingSpinner
      className="absolute inset-0 z-50 bg-bluegray-darker/85"
      borderRadius="rounded-sm md:rounded-md"
      label={loadingLabel}
    />
  );

  const toolbarTop = (
    <ToolbarTop
      name={`tbt-${className}`}
      elements={headerElements}
      leftToolbarGap="gap-1"
    />
  );

  const toolbarBottom = (
    <ToolbarBottom
      ref={footerRef}
      name={`tbb-${className}`}
      elements={footerElements}
    />
  );

  const activationForm = (
    <form
      onSubmit={_onSubmit}
      className={`form-${className} grid px-4 gap-6 text-sm md:text-md text-custom-gray-600 dark:text-pale-white leading-relaxed`}>
      <div
        className="w-full h-auto flex flex-col items-center text-center">
        <div
          className="profile-picture w-fit h-fit rounded-full overflow-hidden shadow-[1px_1px_2px_1px_rgba(105,120,127,1)]!">
          <img
            src={photo}
            alt={`${user.firstName} ${user.lastName}`}
            className="w-20 h-20 object-contain rounded-full border-none"
          />
        </div>
        <div
          className="font-semibold text-sm text-black dark:text-bluegray-lighter tracking-wide mt-2">
          {user?.firstName || user?.lastName ? `${user?.firstName} ${user?.lastName}` : ""}
        </div>
        <div
          className="font-semibold text-xs text-gray-600 dark:text-bluegray-light tracking-tight mt-1">
          {user?.email}
        </div>
      </div>

      <div
        className="w-full h-auto flex flex-col gap-4">
        <div
          className="px-4 py-1 text-sm text-custom-gray-300">
          {pages.activation.text.replace("{email}", user?.email).replace("{expiry}", policies.userPolicy.fields.otp.expiry)}
        </div>

        <div
          className="flex flex-col justify-center items-center self-center min-h-16">
          <OtpInput
            key={`o-${resetKey}`}
            ref={useMergeRefs(otpRef, otpRegisterRef)}
            format="numeric"
            maxLength={userPolicy.otp.maxLength}
            value={wOtp}
            error={errors.otp?.message}
            {...otpRegister}
            prevFieldRef={confirmPasswordRef}
            nextFieldRef={passwordRef}
            onChange={async (_e) => {
              clearErrors("otp");
            }}
            onBlur={async (e) => {
              await otpRegisterOnBlur(e);
              await trigger("otp");
            }}
          />
          {showResend && (
            <div
              className="flex items-center gap-2 text-xs mt-2"
            >
              <button
                onClick={onOtpRequest}
                disabled={isCounting}
                className={`font-medium transition duration-200 ${
                  isCounting
                    ? "text-generic-dull cursor-not-allowed"
                    : "text-blue-600 underline cursor-pointer hover:text-generic-hover hover:scale-105"
                }`}
              >
                Resend
                OTP
              </button>

              {isCounting && (
                <span
                  className="text-generic-dull">({seconds}s)</span>
              )}
            </div>
          )}
        </div>

        <div
          className="flex flex-col justify-center items-center self-center px-1 py-0">
          <InputValidator
            ref={useMergeRefs(passwordRef, passwordRegisterRef)}
            type="password"
            placeholder="New Password*"
            minLength={userPolicy.password.minLength}
            error={errors.password?.message}
            {...passwordRegister}
            onBlur={async (e) => {
              await passwordRegisterOnBlur(e);
              await trigger("password");
            }}
          />
          <PasswordStrengthBar
            className="strength-bar w-[85%]!"
            password={wPassword}
          />
        </div>

        <div
          className="flex justify-center items-center self-center px-1 py-0">
          <InputValidator
            ref={useMergeRefs(confirmPasswordRef, confirmPasswordRegisterRef)}
            type="password"
            placeholder="Confirm Password*"
            minLength={userPolicy.password.minLength}
            error={errors.confirmPassword?.message}
            {...confirmPasswordRegister}
            onChange={async (e) => {
              await confirmPasswordRegisterOnChange(e);
              await trigger("confirmPassword");
            }}
            onBlur={async (e) => {
              await confirmPasswordRegisterOnBlur(e);
              await trigger("confirmPassword");
            }}
          />
        </div>
      </div>
    </form>
  );

  const content = (
    <div
      ref={contentRef}
      onScroll={onScroll}
      className={`
          content-${className}
          flex-1 min-h-0 box-border overflow-y-auto 
          bg-[radial-gradient(circle,#ffffff_0%,#d6d6d6_100%)] 
          dark:bg-[radial-gradient(circle,#030b11b8_0%,#030b11d8_100%)] 
          scrollbar-track-rounded-full scrollbar-thin 
          scrollbar-track-dim-white dark:scrollbar-track-transparent 
          scrollbar-thumb-custom-blue-600 
          scrollbar-hover:scrollbar-thumb-custom-blue-300 
          scrollbar-active:scrollbar-thumb-custom-blue-300
          ${isOverflow ? "pt-4" : "py-4"}
        `}
      style={{
        paddingBottom: isOverflow ? footerHeight + 16 : undefined
      }}
      onWheel={(e) => e.stopPropagation()}
      onTouchMove={(e) => e.stopPropagation()}>
      {activationForm}
    </div>
  );

  const staticFooter = (
    <div>
      {toolbarBottom}
    </div>
  );

  const fixedFooter = (
    <div
      className={`
          absolute bottom-0 left-0 right-0 z-50
          transition-transform duration-300 ease-in-out
          ${showFooter ? "translate-y-0" : "translate-y-full"}
        `}
    >
      {toolbarBottom}
    </div>
  );
  //endregion HTML Elements

  return (
    <div
      className="relative w-full h-full flex flex-col rounded-sm md:rounded-md overflow-hidden"
      onKeyDown={_onKeyDown}
      onWheel={(e) => e.stopPropagation()}>
      {loading && (
        loadingSpinner
      )}

      {toolbarTop}

      {content}

      {!isOverflow ? (
        staticFooter
      ) : (
        fixedFooter
      )}
    </div>
  );
}
